import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from './Navbar';

// const BACKEND_URL = 'http://127.0.0.1:8000';

// One accent per job type — matches JobSearch.jsx so the two pages feel
// like the same product, not a bolted-on afterthought.
const JOB_TYPE_ACCENT = {
  full_time: '#2563EB',
  part_time: '#0EA5E9',
  contract: '#7C3AED',
  internship: '#F59E0B',
};
const accentFor = (type) => JOB_TYPE_ACCENT[type] || '#8B5CF6';

const normalizeSkills = (skills) => {
  if (!skills) return [];
  if (Array.isArray(skills)) return skills.filter(Boolean);
  return String(skills).split(',').map((s) => s.trim()).filter(Boolean);
};

const resolveLogoSrc = (logo) => {
  if (!logo) return null;
  return logo.startsWith('http') ? logo : `${BACKEND_URL}/storage/${logo}`;
};

function SavedJobs() {
  const navigate = useNavigate();
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);
  const [applyingId, setApplyingId] = useState(null);
  const [appliedIds, setAppliedIds] = useState([]);

  const fetchSavedJobs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/saved-jobs');
      setJobs(res.data);
    } catch (err) {
      console.error('Failed to fetch saved jobs', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedJobs();
  }, []);

  useEffect(() => {
    const fetchAppliedJobs = async () => {
      try {
        const res = await api.get('/my-applications');
        const ids = res.data.map((application) => application.job_posting_id);
        setAppliedIds(ids.filter(Boolean));
      } catch (err) {
        console.error('Failed to fetch applied jobs', err);
      }
    };
    fetchAppliedJobs();
  }, []);

  const handleUnsave = async (e, jobId) => {
    e.preventDefault();
    e.stopPropagation();

    setRemovingId(jobId);
    try {
      await api.delete(`/jobs/${jobId}/save`);
      setJobs((prev) => prev.filter((j) => j.id !== jobId));
    } catch (err) {
      console.error('Failed to remove saved job', err);
    } finally {
      setRemovingId(null);
    }
  };

  const handleQuickApply = async (e, jobId) => {
    e.preventDefault();
    e.stopPropagation();

    setApplyingId(jobId);
    try {
      await api.post(`/jobs/${jobId}/apply`, {});
      setAppliedIds((prev) => [...prev, jobId]);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to apply.';
      if (msg.toLowerCase().includes('already applied')) {
        setAppliedIds((prev) => [...prev, jobId]);
      } else {
        alert(msg);
      }
    } finally {
      setApplyingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5FC] to-[#FDF1F9]">
      <Navbar user={user} />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-purple-600 mb-5 transition"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back
        </button>
        <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent mb-1">Saved Jobs</h2>
        <p className="text-gray-500 text-sm mb-6">
          {loading ? 'Loading…' : `${jobs.length} job${jobs.length === 1 ? '' : 's'} saved`}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {loading ? (
            <p className="text-gray-500 md:col-span-2">Loading saved jobs...</p>
          ) : jobs.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center text-gray-400 md:col-span-2">
              <p className="mb-3">You haven't saved any jobs yet.</p>
              <Link
                to="/jobs"
                className="inline-block bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-sm hover:opacity-90 transition"
              >
                Browse jobs
              </Link>
            </div>
          ) : (
            jobs.map((job) => {
              const accent = accentFor(job.job_type);
              const applied = appliedIds.includes(job.id);
              const logoSrc = resolveLogoSrc(job.company_logo || job.employer?.company_logo);

              return (
                <Link
                  key={job.id}
                  to={`/jobs/${job.id}`}
                  className="group relative flex h-full bg-white rounded-2xl shadow-sm hover:shadow-md hover:border-purple-200 border border-gray-100 overflow-hidden transition-all duration-200"
                >
                  {/* Accent spine — colour keys the job type at a glance */}
                  <div className="w-1.5 shrink-0" style={{ backgroundColor: accent }} />

                  <div className="flex-1 p-5 flex flex-col">
                    <div className="flex justify-between items-start gap-4 mb-4">
                      <div className="flex gap-4 min-w-0">
                        {logoSrc ? (
                          <img
                            src={logoSrc}
                            alt={job.company_name}
                            className="w-14 h-14 rounded-xl object-cover shrink-0 border border-gray-100"
                          />
                        ) : (
                          <div
                            className="w-14 h-14 rounded-xl flex items-center justify-center font-bold text-lg shrink-0"
                            style={{ backgroundColor: `${accent}1A`, color: accent }}
                          >
                            {job.company_name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <h3 className="font-bold text-[#0D1B3D] leading-snug rounded-full bg-purple-50 text-purple-600">
                            {job.title}
                          </h3>
                          <p className="text-sm text-gray-500 mt-0.5">{job.company_name}</p>
                          <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-500">
                            <span>📍</span>
                            <span>{job.location || 'Not specified'}</span>
                          </div>

                          {normalizeSkills(job.skills_required).length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {normalizeSkills(job.skills_required).slice(0, 3).map((skill, i) => (
                                <span
                                  key={i}
                                  className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-gray-100 text-gray-600"
                                >
                                  {skill}
                                </span>
                              ))}
                              {normalizeSkills(job.skills_required).length > 3 && (
                                <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-purple-50 text-purple-400">
                                  +{normalizeSkills(job.skills_required).length - 3} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Unsave — a filled star that removes this job on click */}
                      <button
                        onClick={(e) => handleUnsave(e, job.id)}
                        disabled={removingId === job.id}
                        title="Remove from saved"
                        className="shrink-0 text-[#F59E0B] hover:text-amber-600 disabled:opacity-40 text-xl leading-none"
                      >
                        {removingId === job.id ? '…' : '★'}
                      </button>
                    </div>

                    <div className="flex flex-wrap justify-between items-center gap-3 mt-auto pt-4 border-t border-purple-50">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className="text-xs font-bold px-2.5 py-1 rounded-md tabular-nums"
                          style={{ backgroundColor: '#ECFDF5', color: '#059669' }}
                        >
                          ₹{Number(job.salary_min).toLocaleString()} - ₹{Number(job.salary_max).toLocaleString()}
                        </span>
                        <span
                          className="text-xs font-semibold px-2.5 py-1 rounded-md capitalize"
                          style={{ backgroundColor: `${accent}14`, color: accent }}
                        >
                          {job.job_type.replace('_', ' ')}
                        </span>
                        <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-1 rounded-md capitalize">
                          {job.work_mode.replace(/_/g, ' ')}
                        </span>
                        <span className="text-gray-400 text-xs">
                          {job.experience_required}+ yrs exp
                        </span>
                        {job.openings != null && (
                          <span className="text-gray-400 text-xs">
                            · {job.openings} opening{job.openings === 1 ? '' : 's'}
                          </span>
                        )}
                      </div>

                      <button
                        onClick={(e) => handleQuickApply(e, job.id)}
                        disabled={applyingId === job.id || applied}
                        className={`px-5 py-1.5 rounded-full text-sm font-semibold transition shrink-0 shadow-sm ${
                          applied
                            ? 'bg-[#ECFDF5] text-[#10B981] cursor-default'
                            : 'bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:opacity-90 disabled:opacity-50'
                        }`}
                      >
                        {applied ? '✓ Applied' : applyingId === job.id ? 'Applying...' : 'Apply Now'}
                      </button>
                    </div>
                  </div>
                </Link>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default SavedJobs;